import { BatteryFull, BatteryWarning, BellOff, PlugZap } from 'lucide-react';
import { memo, useMemo } from 'react';

import ArrowTooltip from '@/components/custom-ui/customTooltip';
import { cn } from '@/lib/utils';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

type RetailStatusType =
  | 'needStock'
  | 'canTransfer'
  | 'noTrans'
  | 'overStock'
  | boolean
  | undefined;

type RetailStatusBadgeProps = {
  status: RetailStatusType;
  diff?: number;
};

type StatusStyleType = {
  label: string;
  bg: string;
  text: string;
  icon: React.ReactNode;
  border: string;
  glow: string;
};

const RetailStatusBadge = memo(
  ({ status, diff = 0 }: RetailStatusBadgeProps) => {
    const actualDiff = Math.abs(diff || 0);

    // Use useMemo to prevent unnecessary recreations on each render - placed outside any conditional logic
    const typeMapping = useMemo<Record<string, StatusStyleType>>(
      () => ({
        needStock: {
          label: 'يحتاج رصيد',
          bg: 'bg-gradient-to-br from-red-100/80 via-red-200/50 to-red-100/80 dark:from-red-900/30 dark:via-red-800/20 dark:to-red-900/30',
          text: 'text-red-700 dark:text-red-300',
          icon: (
            <BatteryWarning className="h-3.5 w-3.5 rotate-90 transition-all duration-300 group-hover:rotate-0 group-hover:scale-110" />
          ),
          border: 'border-red-200/50 dark:border-red-800/30',
          glow: 'shadow-[0_0_8px_rgba(239,68,68,0.3)] dark:shadow-[0_0_12px_rgba(239,68,68,0.4)]',
        },
        canTransfer: {
          label: 'التحويل من مخزن اخر',
          bg: 'bg-gradient-to-br from-amber-100/80 via-amber-200/50 to-amber-100/80 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/30',
          text: 'text-amber-700 dark:text-amber-300',
          icon: (
            <PlugZap className="h-3.5 w-3.5 rotate-90 transition-all duration-300 group-hover:rotate-0 group-hover:scale-110" />
          ),
          border: 'border-amber-200/50 dark:border-amber-800/30',
          glow: 'shadow-[0_0_8px_rgba(245,158,11,0.3)] dark:shadow-[0_0_12px_rgba(245,158,11,0.4)]',
        },
        noTrans: {
          label: 'لا يوجد حركة',
          bg: 'bg-gradient-to-br from-gray-100/80 via-gray-200/50 to-gray-100/80 dark:from-gray-900/30 dark:via-gray-800/20 dark:to-gray-900/30',
          text: 'text-gray-700 dark:text-gray-300',
          icon: (
            <BellOff className="h-3.5 w-3.5 rotate-90 transition-all duration-300 group-hover:rotate-0 group-hover:scale-110" />
          ),
          border: 'border-gray-200/50 dark:border-gray-800/30',
          glow: 'shadow-[0_0_8px_rgba(107,114,128,0.3)] dark:shadow-[0_0_12px_rgba(107,114,128,0.4)]',
        },
        overStock: {
          label: 'رصيد زيادة',
          bg: 'bg-gradient-to-br from-green-100/80 via-green-200/50 to-green-100/80 dark:from-green-900/30 dark:via-green-800/20 dark:to-green-900/30',
          text: 'text-green-700 dark:text-green-300',
          icon: (
            <BatteryFull className="h-3.5 w-3.5 rotate-90 transition-all duration-300 group-hover:rotate-0 group-hover:scale-110" />
          ),
          border: 'border-green-200/50 dark:border-green-800/30',
          glow: 'shadow-[0_0_8px_rgba(34,197,94,0.3)] dark:shadow-[0_0_12px_rgba(34,197,94,0.4)]',
        },
      }),
      [],
    );

    // Handle boolean or undefined values
    if (typeof status === 'boolean' || status === undefined) {
      return (
        <span className="text-xs text-muted-foreground">
          {status ? 'نشط' : 'غير نشط'}
        </span>
      );
    }

    const typeData = typeof status === 'string' ? typeMapping[status] : null;

    if (!typeData) {
      return <span className="text-xs text-muted-foreground">غير معروف</span>;
    }

    return (
      <ArrowTooltip color="primary" msg={typeData.label}>
        <div
          className={cn(
            'group relative flex h-7 w-14 items-center justify-between rounded-md border px-1.5 font-roboto text-xs font-bold transition-all duration-300 hover:scale-105',
            typeData.bg,
            typeData.text,
            typeData.border,
          )}
        >
          {/* Decorative elements with improved performance */}
          <div className="from-current/30 absolute -left-1 -top-1 h-2 w-2 rounded-full bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></div>
          <div className="from-current/30 absolute -bottom-1 -right-1 h-2 w-2 rounded-full bg-gradient-to-tl to-transparent opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></div>

          {/* Content */}
          <span className="z-1 relative">
            {formatNumberWithCommas(actualDiff)}
          </span>
          <div className="z-1 relative flex items-center justify-center">
            {typeData.icon}
          </div>
        </div>
      </ArrowTooltip>
    );
  },
);

RetailStatusBadge.displayName = 'RetailStatusBadge';

export default RetailStatusBadge;
